import { Offcanvas, Row } from 'react-bootstrap';
import React from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { EditEntityDrawerProps } from './EntityListProps';
import {
  IncidentStrategy,
  useEditIncidentStrategy,
} from './hooks/incidentStrategyHooks';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import FormTextField from './formFields/FormTextField';
import FormTextAreaField from './formFields/FormTextAreaField';
import { Tactic, useTactics } from './hooks/tacticHooks';
import RelationshipPickerField from './formFields/RelationshipPickerField';
import { useIncidentObjectives } from './hooks/incidentObjectiveHooks';
import { ParentRelationships } from './ParentRelationships';
import { useIncidentTactics } from './hooks/incidentTacticHooks';
import { Strategy, useEditStrategy } from './hooks/strategyHooks';
import { INCIDENT } from './ICSConstants';
import { isIncidentStrategy } from './utils/typeguard';
import { StrategyBase } from '../../types/iapBaseTypes';

type FormFields = StrategyBase & {
  tactics?: UUID[];
};

const StrategyEditDrawer: React.FC<
  EditEntityDrawerProps<IncidentStrategy | Strategy>
> = ({ toggle, selectedEntity: editedEntity, scope = INCIDENT }) => {
  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      name: editedEntity?.name ?? '',
      description: editedEntity?.description ?? '',
      tactics: 'tactics' in editedEntity ? (editedEntity.tactics ?? []) : [],
    },
  });
  const { handleSubmit, formState } = form;

  // We don't edit relationships for org resources, so we only need this data when editing an incident
  const isEnabled = scope === INCIDENT;
  const { data: tacticData } = useTactics(isEnabled);
  const { data: incidentTacticOptions } = useIncidentTactics(scope);

  const tacticOptions =
    tacticData
      ?.map((tactic: Tactic) => {
        const matchingIncidentTactic = incidentTacticOptions?.find(
          (incidentTactic) => incidentTactic.tactic_id === tactic.id
        );

        return {
          ...tactic,
          isFromICS: !matchingIncidentTactic,
          archived: matchingIncidentTactic?.archived ?? false,
        };
      })
      .filter((tactic) => !tactic.archived) // Filter out archived tactics
      .sort((a, b) => Number(a.isFromICS) - Number(b.isFromICS)) || [];

  const incidentObjectives = useIncidentObjectives(scope, (ios) =>
    'objectives' in editedEntity
      ? (editedEntity.objectives ?? [])
          .map((ioId: UUID) => ios.find((io) => io.objective_id === ioId))
          .filter((x): x is NonNullable<typeof x> => !!x)
      : []
  );

  const isIncident = isIncidentStrategy(editedEntity, scope);
  const id = isIncident ? editedEntity.strategy_id : editedEntity.id;
  const incidentMutation = useEditIncidentStrategy(id);
  const organizationMutation = useEditStrategy();

  const onNewStrategyDataSubmit = async (formData: FormFields) => {
    let payload;
    if (isIncident) {
      payload = {
        strategy_id: id,
        name: formData.name,
        description: formData.description,
        tactics: formData.tactics ?? [],
        archived: editedEntity.archived,
      };
      await incidentMutation.mutateAsync(payload);
    } else {
      payload = {
        id,
        name: formData.name,
        description: formData.description,
        tactics: editedEntity.tactics ?? [],
        archived: editedEntity.archived,
      };
      await organizationMutation.mutateAsync(payload);
    }
    toggle();
  };

  const toggleActive = async () => {
    if (isIncident) {
      await incidentMutation.mutateAsync({
        ...editedEntity,
        archived: !editedEntity.archived,
      });
    } else {
      await organizationMutation.mutateAsync({
        ...editedEntity,
        archived: !editedEntity.archived,
      });
    }
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Edit Strategy">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onNewStrategyDataSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'The maximum length is 300 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Description"
                fieldName="description"
                rules={{
                  maxLength: {
                    value: 300,
                    message: 'The maximum length is 300 characters',
                  },
                }}
                variant="full"
              />
            </Row>
            {isIncident && (
              <Row>
                <ParentRelationships
                  label="Objectives"
                  parents={incidentObjectives.data}
                />
              </Row>
            )}
            {isIncident && (
              <Row
                style={{
                  maxHeight: '50vh',
                  overflow: 'auto',
                }}
              >
                <RelationshipPickerField<Tactic>
                  label={'Tactics'}
                  fieldName={'tactics'}
                  defaultValue={editedEntity?.tactics ?? []}
                  options={tacticOptions}
                />
              </Row>
            )}
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--tertiary button--reverse"
                onClick={toggleActive}
              >
                {(!editedEntity.archived && 'Deactivate') || 'Activate'}
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={
                  (isIncident
                    ? incidentMutation.isPending
                    : organizationMutation.isPending) ||
                  !formState.isDirty ||
                  editedEntity.archived
                }
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default StrategyEditDrawer;
